export type NarratorTier = 'milestone' | 'event' | 'ambient'

export interface LineDef {
  text: string
  voiceOverrides?: { stability?: number; style?: number }
}

export interface KeyDef {
  tier: NarratorTier
  oneShot?: boolean
  lines: LineDef[]
}

export interface CallbackChain {
  first: NarratorKey
  second: NarratorKey
  windowSec: number
}

export const NARRATOR_LINES = {
  // ── Milestones ───────────────────────────────────────────────────────────
  first_eat:           { tier: 'milestone', oneShot: true, lines: [{ text: 'Good. Keep going.' }] },
  first_dna_10:        { tier: 'milestone', oneShot: true, lines: [{ text: 'Some progress, I suppose.' }] },
  first_skill:         { tier: 'milestone', oneShot: true, lines: [{ text: 'Interesting choice.' }] },
  first_predator_seen: { tier: 'milestone', oneShot: true, lines: [{ text: 'Uh-oh.' }] },
  first_death:         { tier: 'milestone', oneShot: true, lines: [{ text: 'Noted.' }] },
  first_death_orb:     { tier: 'milestone', oneShot: true, lines: [{ text: "Aren't you going to pick that up?" }] },
  first_ally:          { tier: 'milestone', oneShot: true, lines: [{ text: 'Aw. A new little friend.' }] },
  first_predator_eaten: { tier: 'milestone', oneShot: true, lines: [{ text: 'It used to eat things like you.' }] },
  first_growth_spurt:  { tier: 'milestone', oneShot: true, lines: [{ text: 'The body has ideas. Pick one.' }] },
  boss_spotted:        { tier: 'milestone', oneShot: true, lines: [{ text: "Oh. That's... quite large." }] },
  boss_wake:           { tier: 'milestone', lines: [{ text: "Oh. It's awake." }] },
  boss_phase2:         { tier: 'event',     lines: [{ text: 'That seems to have upset it.' }] },
  boss_killed:         { tier: 'milestone', lines: [{ text: 'Specimen... terminated.' }] },

  // ── Events ───────────────────────────────────────────────────────────────
  predator_windup: { tier: 'event', lines: [
    { text: 'Be careful.' },
    { text: 'Hmm.' },
    { text: 'It sees you.' },
    { text: "This won't end well." },
    { text: "I'm picking up a lot of toxic masculinity from this one." },
  ]},
  ally_died: { tier: 'event', lines: [
    { text: 'Returned to the medium.' },
    { text: 'Good grief.' },
    { text: 'Better to have loved and lost, than never to have loved at all.', voiceOverrides: { style: 0.4 } },
    { text: 'Parting is such sweet sorrow.', voiceOverrides: { style: 0.4 } },
    { text: 'Nice knowing you, friend.' },
    { text: 'Adios muchacho.' },
  ]},
  allies_wiped: { tier: 'event', lines: [
    { text: 'Where did everybody go.' },
    { text: 'Alone again.' },
    { text: 'Just you, then.' },
  ]},
  player_low_health: { tier: 'event', lines: [
    { text: 'This is not going well.' },
  ]},
  death: { tier: 'event', lines: [
    { text: 'Oopsies.' },
    { text: 'Oh nooooo.' },
    { text: 'Back to the medium.' },
    { text: 'These things happen.' },
    { text: 'Disappointing.' },
    { text: 'Looks like I get to go home early today.' },
    { text: "The undiscover'd country from whose bourn no traveller returns.", voiceOverrides: { style: 0.4 } },
    { text: 'To die, to sleep — no more.', voiceOverrides: { style: 0.4 } },
  ]},
  dna_orb_pickup: { tier: 'event', lines: [
    { text: 'There it is.' },
    { text: 'Protein shake.' },
    { text: 'Did you know that one?' },
    { text: 'Nom nom nom.', voiceOverrides: { stability: 0.4, style: 0.1 } },
  ]},
  engulf_activated: { tier: 'event', lines: [
    { text: 'Phagocytosis. Lovely.' },
    { text: 'Super saiyan!', voiceOverrides: { stability: 0.4, style: 0.1 } },
    { text: 'Ohh this is going to be good.', voiceOverrides: { stability: 0.4, style: 0.1 } },
    { text: 'Watch out world, here we come.', voiceOverrides: { stability: 0.4, style: 0.1 } },
  ]},
  shop_open: { tier: 'event', lines: [
    { text: "Don't you already have one of those?" },
    { text: 'Mind the prices.' },
    { text: 'Decisions, decisions.' },
  ]},
  shop_close_no_buy: { tier: 'event', lines: [
    { text: 'Window shopping, then.' },
    { text: 'Saving. Prudent.' },
    { text: "Darling, if you have to ask, you can't afford it." },
  ]},

  // ── Mutation pickups ─────────────────────────────────────────────────────
  mutation_pickup: { tier: 'event', lines: [
    { text: 'Folded in.' },
    { text: 'Another acquisition.' },
    { text: 'The membrane reorganizes.' },
    { text: 'Genome was a draft anyway.' },
  ]},
  mutation_capped: { tier: 'event', lines: [
    { text: 'The locus is full.' },
    { text: 'Receptor sites saturated.' },
  ]},
  mutation_bouquet_first:         { tier: 'event', oneShot: true, lines: [{ text: 'Bouquet. Decorative. The neighborhood will notice.' }] },
  mutation_static_cling_first:    { tier: 'event', oneShot: true, lines: [{ text: 'Static cling. Loose particles will attend.' }] },
  mutation_nucleus_envy_first:    { tier: 'event', oneShot: true, lines: [{ text: 'Nucleus envy. Eats more. Holds less.' }] },
  mutation_wet_socks_first:       { tier: 'event', oneShot: true, lines: [{ text: 'Wet socks. The condition does not improve.' }] },
  mutation_borrowed_teeth_first:  { tier: 'event', oneShot: true, lines: [{ text: 'Borrowed teeth. The friends will manage them.' }] },
  mutation_eyes_everywhere_first: { tier: 'event', oneShot: true, lines: [{ text: 'Eyes everywhere. The specimen is now more interested.' }] },

  // ── Evolution (tier-up at the storekeeper) ───────────────────────────────
  // Authored via amoeba-voice 2026-05-02. Class 4 (Narrator Tier) — register
  // lifts at the moment but doesn't go operatic. "More to it" hints the
  // descent toward Heck without naming it; the narrator notices, doesn't
  // know.
  evolution_tier2: { tier: 'milestone', oneShot: true, lines: [
    { text: 'The specimen has elaborated. The slide, it appears, has more to it.' },
  ]},
  // Superseded by the per-form speciation reveals below: the tier-3 evolve
  // now resolves into a critter rather than budding horns, so the shop no
  // longer fires this key (TIERS_WITH_NARRATOR_LINE in skill-shop.ts). Left
  // in place so its audio isn't pruned if the descent framing returns.
  evolution_tier3: { tier: 'milestone', oneShot: true, lines: [
    { text: 'Third elaboration. The specimen has budded small horns. The record does not account for them.' },
    { text: 'It has grown horns. Vestigial, for now. The descent proceeds on schedule.' },
  ]},

  // ── Speciation reveal (tier-3 — "what did I become?") ────────────────────
  // Fired by systems/narrator.ts on lineage.speciated, keyed on the resolved
  // form. Milestone tier: the register lifts, one earned beat per run. The
  // narrator names the shape and, per the deadpan rule, treats each as a
  // finding it did not predict. Authored via amoeba-voice 2026-07-05.
  speciation_whiskerkit: { tier: 'milestone', oneShot: true, lines: [
    { text: 'The specimen has settled on a shape. Whiskers, of all things. It specializes in nothing, and thrives.' },
  ]},
  speciation_narwhalen: { tier: 'milestone', oneShot: true, lines: [
    { text: 'A single tusk has emerged. The specimen appears to have decided the answer to most questions is forward.' },
  ]},
  speciation_anemonaut: { tier: 'milestone', oneShot: true, lines: [
    { text: 'The specimen has stopped traveling and begun to wave. A whole crown of it. This is, apparently, the plan.' },
  ]},
  speciation_frillgill: { tier: 'milestone', oneShot: true, lines: [
    { text: 'External gills, worn without embarrassment. The specimen has elected never to finish growing up. Noted, and respected.' },
  ]},
  speciation_lanternet: { tier: 'milestone', oneShot: true, lines: [
    { text: 'It has grown its own light and hung it before its own face. The dark did not object.' },
  ]},

  // ── Biome crossings (Deep Water — first descent step) ────────────────────
  // Once-per-session via main.ts seenBiomeEvents; NOT oneShot in narrator
  // state (that persists across reloads). Two variants each — narrator
  // shuffle bag picks randomly across sessions for variety. Authored via
  // amoeba-voice 2026-05-02.
  biome_enter_deep: { tier: 'event', lines: [
    { text: 'The medium is cooler here. The cultures, unfamiliar. Note logged.' },
    { text: 'The slide darkens. The caustics no longer reach. Interesting.' },
  ]},
  biome_leave_deep: { tier: 'event', lines: [
    { text: 'The specimen has resurfaced. Survival instinct is noted.' },
    { text: 'Back to the shallows. The survey expected this.' },
  ]},
  biome_enter_mushroom: { tier: 'milestone', lines: [
    { text: 'The substrate is loam now. The spore-bearers exhale without warning.' },
    { text: 'The mycelium has noticed you. It is in no hurry.' },
  ]},

  // ── Shortcut doors (B3) — authored via amoeba-voice 2026-07-09 ──────────
  // Blocked: the first home-side press of a sealed shortcut. One geography
  // lesson, once. Opened: fires per door (small pool for future doors).
  door_blocked: { tier: 'milestone', oneShot: true, lines: [
    { text: 'Sealed. This kind opens from the other side.' },
  ]},
  door_opened: { tier: 'milestone', lines: [
    { text: 'The membrane yields. Home is closer than it was.' },
    { text: 'It remembers you now. It will stay open.' },
  ]},

  // ── Gate seals (B4) — each blocked line NAMES the key; that's the whole
  // telegraph. Authored via amoeba-voice 2026-07-09.
  seal_spore_blocked: { tier: 'milestone', oneShot: true, lines: [
    { text: 'Spore-crusted. It answers only to Sporulation.' },
  ]},
  seal_charge_blocked: { tier: 'milestone', oneShot: true, lines: [
    { text: 'A standing charge. Galvanism would disagree with it.' },
  ]},
  seal_pass_blocked: { tier: 'milestone', oneShot: true, lines: [
    { text: 'Official seal. Documentation required.' },
  ]},
  hallpass_granted: { tier: 'milestone', oneShot: true, lines: [
    { text: 'It was carrying paperwork. The hall pass is yours.' },
  ]},

  // ── Town portal (B5) — authored via amoeba-voice 2026-07-09. Event tier:
  // trips fire often, keep the pool dry and short. Audio not yet generated
  // (captions only until the next gen-narrator run).
  portal_cast: { tier: 'event', lines: [
    { text: 'Chemotaxis. It remembered the way home.' },
    { text: 'Gone. Home, apparently.' },
    { text: 'A shortcut. Through everything.' },
  ]},
  portal_return: { tier: 'event', lines: [
    { text: 'Back where it left off.' },
    { text: 'The current runs both ways.' },
  ]},

  // ── Skill purchase etymology (one-shot per skill, event tier) ────────────
  skill_flagellate:  { tier: 'event', oneShot: true, lines: [{ text: "Flagella. From the Latin for 'whip.'" }] },
  skill_autophagy:   { tier: 'event', oneShot: true, lines: [{ text: 'Self-consumption. A bold strategy.' }] },
  skill_hypertrophy: { tier: 'event', oneShot: true, lines: [{ text: "Size isn't everything. Usually." }] },
  skill_pseudopod:   { tier: 'event', oneShot: true, lines: [{ text: 'A temporary protrusion.' }] },
  skill_membrane:    { tier: 'event', oneShot: true, lines: [{ text: 'Structural integrity. Good thinking.' }] },

  // ── Ambient ──────────────────────────────────────────────────────────────
  idle_shopping_list: { tier: 'ambient', lines: [{ text: 'Milk. Bread. Agar. Petri dishes...' }] },
  idle_anecdote_1:    { tier: 'ambient', lines: [{ text: 'I had a colleague who studied tardigrades. Never came back.' }] },
  idle_anecdote_2:    { tier: 'ambient', lines: [{ text: "My wife says I work too much. She's probably right." }] },
  idle_anecdote_3:    { tier: 'ambient', lines: [{ text: 'Fascinating. Truly. Yes.' }] },
  idle_bathroom:      { tier: 'ambient', lines: [{ text: "I'll be right back. I have to go tinkle." }] },
  idle_observation:      { tier: 'ambient', lines: [{ text: 'The cytoplasm is streaming. As expected.' }] },
  idle_quiet:            { tier: 'ambient', lines: [{ text: 'Quiet today.' }] },
  idle_metachronal:      { tier: 'ambient', lines: [{ text: 'The cilia exhibit the standard metachronal pattern. Textbook.' }] },
  idle_pseudopod_noted:  { tier: 'ambient', lines: [{ text: 'A pseudopod extended briefly. It seemed to reconsider.' }] },
  idle_flagellate_drift: { tier: 'ambient', lines: [{ text: 'The flagellate cultures are uncoordinated. As expected.' }] },
} as const satisfies Record<string, KeyDef>

export type NarratorKey = keyof typeof NARRATOR_LINES

export const CALLBACK_CHAINS: CallbackChain[] = [
  { first: 'death',               second: 'dna_orb_pickup',    windowSec: 30 },
  { first: 'first_predator_seen', second: 'predator_windup',   windowSec: 8  },
  { first: 'first_skill',         second: 'skill_flagellate',  windowSec: 1  },
  { first: 'first_skill',         second: 'skill_autophagy',   windowSec: 1  },
  { first: 'first_skill',         second: 'skill_hypertrophy', windowSec: 1  },
  { first: 'first_skill',         second: 'skill_pseudopod',   windowSec: 1  },
  { first: 'first_skill',         second: 'skill_membrane',    windowSec: 1  },
  { first: 'allies_wiped',        second: 'death',             windowSec: 5  },
]

export const AMBIENT_KEYS: NarratorKey[] = [
  'idle_shopping_list', 'idle_anecdote_1', 'idle_anecdote_2',
  'idle_anecdote_3', 'idle_bathroom', 'idle_observation', 'idle_quiet',
  'idle_metachronal', 'idle_pseudopod_noted', 'idle_flagellate_drift',
]
